if (!customElements.get('product-form')) {
  customElements.define('product-form', class ProductForm extends HTMLElement {
    constructor() {
      super();

      this.form = this.querySelector('form');
      if (!this.form) {
        console.warn('product-form: form element not found', this);
        return;
      }

      const variantInput = this.variantIdInput;
      if (variantInput) {
        variantInput.disabled = false;
      }

      this.form.addEventListener('submit', this.onSubmitHandler.bind(this));

      const cartBehavior = window.themeSettings?.cartBehavior || 'drawer';
      if (cartBehavior === 'drawer') {
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      } else {
        this.cart = null;
      }

      this.submitButton = this.querySelector('[type="submit"]');
      this.submitButtonText = this.submitButton
        ? this.submitButton.querySelector('span')
        : null;

      this.cartUpdateUnsubscriber = null;
    }

    connectedCallback() {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          const variantId = this.variantIdInput?.value;
          if (!variantId) return;
          if (event?.variantId && event.variantId.toString() !== variantId.toString()) return;
          this.updateCartQuantity();
        });
      }
    }

    disconnectedCallback() {
      this.cartUpdateUnsubscriber?.();
    }

    onSubmitHandler(evt) {
      evt.preventDefault();
      
      if (this.submitButton.getAttribute('aria-disabled') === 'true') return;
      this.cartShouldStayEmpty = false;
      this.error = false;
      delete this.dataset.cartAddErrorVariantId;
      delete this.dataset.cartAddErrorMessage;

      this.handleErrorMessage();
      this.submitButton.setAttribute('aria-disabled', true);
      this.submitButton.classList.add('loading');
      
      const spinner = this.querySelector('.loading__spinner');
      if (spinner) spinner.classList.remove('hidden');

      const config = {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript'
        }
      };

      const formData = new FormData(this.form);
      const qtyInput = this.form.querySelector('input[name="quantity"]') || this.querySelector('input[name="quantity"]');
      if (qtyInput) formData.set('quantity', qtyInput.value || 1);
      this.removeEmptyLineItemProperties(formData);

      if (this.cart) {
        formData.append('sections', this.cart.getSectionsToRender().map((section) => section.id));
        formData.append('sections_url', window.location.pathname);
        this.cart.setActiveElement(document.activeElement);
      }
      
      config.body = formData;

      fetch(`${window.Shopify.routes.root}cart/add.js`, config)
        .then((response) => response.json())
        .then(async (response) => {
          const isSubscriptionResponse = Boolean(response.sections) && !response.id && !response.key;
          const isSellingPlanRequiredError = this.isSellingPlanRequiredError(response);

          if (response.status) {
            const recipientForm = this.querySelector('recipient-form');
            const hasStructuredErrors = response.description && typeof response.description === 'object';

            if (recipientForm && hasStructuredErrors && typeof recipientForm.displayErrors === 'function') {
              recipientForm.displayErrors('', response.description);
              this.handleErrorMessage(false);
              this.error = false;
              this.dataset.cartAddErrorVariantId = this.variantIdInput?.value || '';
              this.dataset.cartAddErrorMessage = response.message || 'There was an error adding this item to your cart.';
              this.dispatchCartAddEvent('product-form:error', response);
              return;
            }

            const fallbackMessage =
              typeof response.description === 'string'
                ? response.description
                : (response.message || 'There was an error adding this item to your cart.');

            this.handleErrorMessage(fallbackMessage);

            const soldOutMessage = this.submitButton.querySelector('.sold-out-message');
            const soldOutMessageVisible = soldOutMessage && !soldOutMessage.classList.contains('hidden');
            if (soldOutMessage) {
              this.submitButton.setAttribute('aria-disabled', 'true');
              this.submitButton.disabled = true;
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
            }

            this.error = true;
            this.dataset.cartAddErrorVariantId = this.variantIdInput?.value || '';
            this.dataset.cartAddErrorMessage = fallbackMessage;
            this.dispatchCartAddEvent('product-form:error', response);
            if (isSellingPlanRequiredError) {
              this.cartShouldStayEmpty = true;
              return;
            }

            if (this.cart && !soldOutMessageVisible) {
              if (response.sections && typeof response.sections === 'object') {
                this.cart.renderContents(response, false);
              } else {
                this.updateCartOnError();
              }
            }

            return;
          } else if (!this.cart) {
            window.location = window.Shopify.routes.root + 'cart';
            return;
          }

          if (isSubscriptionResponse) {
            this.cartShouldStayEmpty = true;
            this.error = false;
            return;
          }

          this.resetQuantity();

          if (!this.error) {
            this.cart.renderContents(response);
            
            await this.updateCartQuantity(response);
            if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                cartData: response,
                productVariantId: this.variantIdInput?.value,
                variantId: this.variantIdInput?.value
              });
            }
          }
          
          this.error = false;
          this.dispatchCartAddEvent('product-form:added', response);
        })
        .catch((error) => {
          console.error('Error:', error);
          this.dispatchCartAddEvent('product-form:error', error);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          
          if (this.cart && this.cart.classList.contains('is-empty') && !this.error && !this.cartShouldStayEmpty) {
            this.cart.classList.remove('is-empty');
          }

          const soldOutVisible = this.submitButton.querySelector('.sold-out-message:not(.hidden)');
          if (!soldOutVisible) {
            this.submitButton.removeAttribute('aria-disabled');
          }
          
          const spinner = this.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
        });
    }

    handleErrorMessage(errorMessage = false) {
      this.errorMessageWrapper = this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
      
      if (!this.errorMessageWrapper) return;
      
      this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

      this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

      if (errorMessage) {
        this.errorMessage.textContent = errorMessage;
      }
    }

    dispatchCartAddEvent(type, response) {
      this.dispatchEvent(new CustomEvent(type, {
        bubbles: true,
        detail: {
          response,
          variantId: this.variantIdInput?.value
        }
      }));
    }

    get variantIdInput() {
      return this.form.querySelector('[name=id]');
    }

    resetQuantity() {
      const quantityInput = this.form.querySelector('input[name="quantity"]');
      if (quantityInput) {
        const minValue = quantityInput.getAttribute('data-min') || quantityInput.min || '1';
        quantityInput.value = minValue;
        
        const quantityInputElement = quantityInput.closest('quantity-input');
        if (quantityInputElement && typeof quantityInputElement.validateQtyRules === 'function') {
          quantityInputElement.validateQtyRules();
        }
      }
    }

    async updateCartQuantity(cartResponse) {
      const variantId = this.variantIdInput?.value;
      if (!variantId) return;

      let cartQty = 0;
      try {
        const response = await fetch(`${window.Shopify.routes.root}cart.js`);
        const cart = await response.json();
        const cartItem = cart.items?.find(item => item.variant_id.toString() === variantId.toString());
        cartQty = cartItem ? cartItem.quantity : 0;
      } catch (e) {
        console.error('Error fetching cart:', e);
        return;
      }

      const rulesCart = this.querySelector('.quantity__rules-cart');
      if (rulesCart) {
        const spinner = rulesCart.querySelector('.loading__spinner');
        if (spinner) spinner.classList.add('hidden');
        
        const textSpan = rulesCart.querySelector('[data-cart-quantity-template]') || rulesCart.querySelector('span span');
        if (textSpan) {
          const template = textSpan.dataset?.cartQuantityTemplate;
          textSpan.textContent = template
            ? template.replace('[quantity]', cartQty)
            : `(${cartQty} in cart)`;
        }
        
        rulesCart.classList.toggle('hidden', cartQty === 0);
      }
    }

    async updateCartOnError() {
      if (!this.cart) return;

      try {
        const cartResponse = await fetch(`${window.Shopify.routes.root}cart.js`);
        const cart = await cartResponse.json();
        
        if (cart && cart.item_count !== undefined) {
          if (typeof this.cart.updateCartCount === 'function') {
            this.cart.updateCartCount(cart.item_count);
          }
        }

        await this.updateCartQuantity();

        const sections = this.cart.getSectionsToRender();
        if (sections && sections.length > 0) {
          const sectionIds = sections.map((section) => section.id).join(',');
          const sectionUrl = `${window.Shopify.routes.root}cart?section_id=${sectionIds}`;
          
          const sectionResponse = await fetch(sectionUrl);
          const sectionText = await sectionResponse.text();
          const parser = new DOMParser();
          const newDocument = parser.parseFromString(sectionText, 'text/html');

          sections.forEach((section) => {
            const sectionId = section.id;
            const currentElement = document.querySelector(section.selector) || document.getElementById(sectionId);
            if (!currentElement) return;

            const newElement = newDocument.querySelector(section.selector) || newDocument.getElementById(sectionId);
            if (newElement) {
              currentElement.innerHTML = newElement.innerHTML;
            }
          });

          this.cart.cacheElements();
          this.cart.bindPanelEvents();
          this.cart.assignTriggers();
        }
      } catch (error) {
        console.error('Error updating cart on error:', error);
      }
    }

    isSellingPlanRequiredError(response) {
      if (!response) return false;

      const hasSellingPlanKey =
        (response.errors && typeof response.errors === 'object' && 'selling_plan' in response.errors) ||
        (response.description && typeof response.description === 'object' && 'selling_plan' in response.description);

      const message = [
        typeof response.message === 'string' ? response.message : '',
        typeof response.description === 'string' ? response.description : '',
      ]
        .join(' ')
        .toLowerCase();

      const hasSellingPlanText = message.includes('selling plan') || message.includes('subscription');

      return Boolean(hasSellingPlanKey || hasSellingPlanText);
    }

    removeEmptyLineItemProperties(formData) {
      if (!(formData instanceof FormData)) return;

      for (const [key, value] of formData.entries()) {
        if (!key.startsWith('properties[')) continue;

        const normalizedValue = typeof value === 'string' ? value.trim() : value;
        if (normalizedValue === '') {
          formData.delete(key);
        }
      }
    }
  });
}